import type { WebsitePropertyDetails } from '../../server/website-property';
import { safeInventoryLink } from '../lib/inventory';
import { sourceTime } from '../lib/sourceSync';

export function PropertySourceDetails({
  details,
  updatedAt,
}: {
  details?: WebsitePropertyDetails;
  updatedAt?: string;
}) {
  if (!details) return null;
  const facts = [
    ['Website publication', details.publicationStatus],
    ['Listing status', details.status],
    [
      'Source availability',
      details.available === undefined
        ? undefined
        : details.available
          ? 'Available · verification required'
          : 'Unavailable',
    ],
    ['Land area', details.totalLandArea],
    ['Floors', details.floors],
    ['Towers', details.towers],
    ['Total units', details.totalUnits],
    ['Building age', details.buildingAge?.join(', ')],
  ].filter(([, value]) => value !== undefined && value !== '');
  const brochure = safeInventoryLink(details.brochure?.url);
  const images = (details.images || [])
    .map(safeInventoryLink)
    .filter((url): url is string => !!url);
  return (
    <section aria-label="Website property details" className="property-source-details">
      <h3>Website property details</h3>
      {sourceTime(updatedAt) && (
        <p>
          Source updated {sourceTime(updatedAt)}. Availability verification is recorded separately.
        </p>
      )}
      {!!facts.length && (
        <dl className="property-source-facts">
          {facts.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      )}
      {!!details.sizesOfFlats?.length && (
        <div>
          <h4>Sizes and configurations from the website</h4>
          <ul>
            {details.sizesOfFlats.map((size, index) => (
              <li key={index}>{size}</li>
            ))}
          </ul>
        </div>
      )}
      {brochure && (
        <p>
          <a href={brochure} target="_blank" rel="noopener noreferrer">
            Open {details.brochure?.filename || 'property brochure'} ↗
          </a>
        </p>
      )}
      {!!images.length && (
        <div className="property-source-gallery">
          {images.map((url, index) => (
            <a href={url} key={url} target="_blank" rel="noopener noreferrer">
              <img src={url} alt={`Property photo ${index + 1}`} loading="lazy" />
            </a>
          ))}
        </div>
      )}
      {!!details.resaleUnits?.length && (
        <div>
          <h4>Resale units supplied by the website</h4>
          <p>
            These unit details belong to this project. Each unit needs its own confirmed total price
            and verification before buyer matching.
          </p>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>UNIT</th>
                  <th>FLOOR</th>
                  <th>BEDROOMS</th>
                  <th>AREA</th>
                  <th>SOURCE AVAILABILITY</th>
                  <th>PHOTOS</th>
                </tr>
              </thead>
              <tbody>
                {details.resaleUnits.map((unit) => (
                  <tr key={unit.id}>
                    <td>{unit.flatNumber || unit.id}</td>
                    <td>{unit.floor ?? 'Not supplied'}</td>
                    <td>{unit.bedrooms ?? 'Not supplied'}</td>
                    <td>
                      {unit.area ? `${unit.area.toLocaleString('en-IN')} sq. ft.` : 'Not supplied'}
                    </td>
                    <td>
                      {unit.available === undefined
                        ? 'Not supplied'
                        : unit.available
                          ? 'Available · verify'
                          : 'Unavailable'}
                    </td>
                    <td>
                      {(unit.photos || [])
                        .map(safeInventoryLink)
                        .filter((url): url is string => !!url)
                        .map((url, index) => (
                          <a href={url} key={index} target="_blank" rel="noopener noreferrer">
                            Photo {index + 1}{' '}
                          </a>
                        ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {[
        ['Nearby schools', details.nearbySchools],
        ['Nearby hospitals', details.nearbyHospitals],
        ['Nearby malls', details.nearbyMalls],
      ].map(([label, items]) =>
        Array.isArray(items) && items.length ? (
          <div key={String(label)}>
            <h4>{String(label)}</h4>
            <p>{items.join(' · ')}</p>
          </div>
        ) : null,
      )}
    </section>
  );
}
